/**
 * SEODashboard — AFAQ-OS SEO Intelligence Module
 * - Dashboard loads from Redis cache (instant)
 * - Background generation with live progress bar
 * - Enzo/Lizze priority always first
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import ScoreGauge from "../seo/ScoreGauge";
import SEOAudit from "../seo/SEOAudit";
import ApprovalQueue from "../seo/ApprovalQueue";
import BlogGenerator from "../seo/BlogGenerator";
import ComplianceChecker from "../seo/ComplianceChecker";
import "../../styles/seo.css";

const TABS = [
  { id: "overview",   label: "Overview",   icon: "📊" },
  { id: "audit",      label: "SEO Audit",  icon: "🔍" },
  { id: "generate",   label: "Generate",   icon: "⚡" },
  { id: "queue",      label: "Approval",   icon: "✅" },
  { id: "blog",       label: "Blog",       icon: "✍️" },
  { id: "compliance", label: "Compliance", icon: "🛡️" },
];

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color }) {
  return (
    <div className="seo-kpi-card">
      <div className="seo-kpi-value" style={{ color: color || "var(--seo-green)" }}>
        {value ?? "—"}
      </div>
      <div className="seo-kpi-label">{label}</div>
      {sub && <div className="seo-kpi-sub">{sub}</div>}
    </div>
  );
}

// ── Grade Distribution Bar ─────────────────────────────────────────────────
function GradeBar({ distribution = {} }) {
  const grades = ["A+", "A", "B", "C", "D", "F"];
  const colors = { "A+": "#00ff88", A: "#00ff88", B: "#00cfaa", C: "#f5c518", D: "#ff8c42", F: "#ff4d6d" };
  const total  = Object.values(distribution).reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="seo-grade-bar-wrap">
      {grades.map(g => {
        const count = distribution[g] || 0;
        const pct   = (count / total) * 100;
        return (
          <div key={g} className="seo-grade-bar-col" style={{ width: `${pct}%` }}>
            <div className="seo-grade-bar-fill" style={{ background: colors[g] }} />
            {pct > 5 && <div className="seo-grade-bar-label">{g}: {count}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ── Progress Bar ───────────────────────────────────────────────────────────
function ProgressBar({ pct, label, phase, current }) {
  const phaseColor = phase === "priority" ? "#00ff88" : "var(--seo-purple)";
  const phaseName  = phase === "priority" ? "🏆 Enzo / Lizze (Priority)" : "📦 Standard Products";
  return (
    <div className="seo-progress-wrap">
      <div className="seo-progress-header">
        <span className="seo-progress-phase">{phaseName}</span>
        <span className="seo-progress-pct">{pct}%</span>
      </div>
      <div className="seo-progress-track">
        <div
          className="seo-progress-fill"
          style={{ width: `${pct}%`, background: phaseColor, transition: "width 0.4s ease" }}
        />
      </div>
      {current && (
        <div className="seo-progress-current">
          ⚙️ Processing: <em>{current}</em>
        </div>
      )}
      {label && <div className="seo-progress-label">{label}</div>}
    </div>
  );
}

// ── Generate Panel ─────────────────────────────────────────────────────────
function GeneratePanel({ onJobComplete }) {
  const [mode, setMode]       = useState("priority");
  const [job, setJob]         = useState(null);   // { job_id, total, ... }
  const [status, setStatus]   = useState(null);   // live poll result
  const [starting, setStarting] = useState(false);
  const [error, setError]     = useState(null);
  const pollRef               = useRef(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPoll = (jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`/api/seo/generate/status/${jobId}`);
        const d = await r.json();
        if (d.success) {
          setStatus(d);
          if (d.status === "done" || d.status === "failed") {
            stopPoll();
            if (d.status === "done") onJobComplete && onJobComplete(d);
          }
        }
      } catch (e) { /* keep polling */ }
    }, 2000);
  };

  useEffect(() => () => stopPoll(), []);

  const handleStart = async () => {
    setStarting(true);
    setError(null);
    setJob(null);
    setStatus(null);
    try {
      const r = await fetch("/api/seo/generate/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode }),
      });
      const d = await r.json();
      if (d.success) {
        setJob(d);
        startPoll(d.job_id);
      } else {
        setError(d.detail || "Failed to start job");
      }
    } catch (e) {
      setError("Cannot reach server — check backend");
    } finally {
      setStarting(false);
    }
  };

  const isRunning = status && status.status === "running";
  const isDone    = status && status.status === "done";
  const pct       = status?.progress_pct || 0;

  return (
    <div className="seo-generate-panel">
      <div className="seo-card" style={{ marginBottom: 16 }}>
        <div className="seo-card-header">⚡ AI SEO Generation</div>
        <p className="seo-generate-desc">
          Generates premium SEO content (titles, descriptions, alt texts, schema) using DeepSeek AI.
          All output goes to the <strong>Approval Queue</strong> — nothing touches Shopify until you approve.
        </p>

        {/* Mode selector */}
        <div className="seo-mode-row">
          {[
            { id: "priority", label: "🏆 Enzo + Lizze Only", desc: "~30 products · fastest · highest impact" },
            { id: "worst",    label: "📉 Priority + Worst 20", desc: "~50 products · balanced run" },
            { id: "all",      label: "🌐 Full Catalogue",      desc: "~395 products · ~25 minutes" },
          ].map(m => (
            <div
              key={m.id}
              className={`seo-mode-card ${mode === m.id ? "active" : ""}`}
              onClick={() => !isRunning && setMode(m.id)}
            >
              <div className="seo-mode-label">{m.label}</div>
              <div className="seo-mode-desc">{m.desc}</div>
            </div>
          ))}
        </div>

        {error && <div className="seo-error" style={{ marginTop: 12 }}>{error}</div>}

        {!isRunning && !isDone && (
          <button
            className="seo-btn seo-btn-primary"
            style={{ marginTop: 16, width: "100%" }}
            onClick={handleStart}
            disabled={starting}
          >
            {starting ? "⏳ Starting…" : "⚡ Start Generation"}
          </button>
        )}
      </div>

      {/* Live progress */}
      {(isRunning || isDone) && status && (
        <div className="seo-card">
          <div className="seo-card-header">
            {isDone ? "✅ Generation Complete" : "⚙️ Generating…"}
          </div>
          <ProgressBar
            pct={pct}
            phase={status.phase}
            current={status.current}
            label={
              isDone
                ? `Done — ${status.queued || 0} changes queued for approval`
                : `${status.done || 0} of ${status.total || 0} products processed`
            }
          />
          <div className="seo-progress-stats">
            <span className="seo-badge-green">{status.done || 0} done</span>
            <span className="seo-badge-orange">{status.queued || 0} queued</span>
            {(status.failed || 0) > 0 && (
              <span className="seo-badge-red">{status.failed} failed</span>
            )}
          </div>
          {isDone && (
            <button
              className="seo-btn seo-btn-primary"
              style={{ marginTop: 12, width: "100%" }}
              onClick={() => { setJob(null); setStatus(null); }}
            >
              Run Another Job
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Worst Products list ────────────────────────────────────────────────────
function WorstProductsList({ products = [], onGenerate }) {
  return (
    <div className="seo-worst-card">
      <div className="seo-card-header">
        <span>Needs SEO Attention</span>
        <button className="seo-btn seo-btn-primary seo-btn-sm" onClick={onGenerate}>
          ⚡ Generate
        </button>
      </div>
      {products.map(p => (
        <div key={p.product_id || p.id} className="seo-worst-row">
          <ScoreGauge score={p.overall_score} size={44} />
          <div className="seo-worst-info">
            <div className="seo-worst-title">
              {p.is_priority && <span className="seo-priority-tag">ENZO/LIZZE</span>}
              {p.title}
            </div>
            <div className="seo-worst-meta">{p.product_type} · {p.vendor}</div>
          </div>
        </div>
      ))}
      {products.length === 0 && (
        <div className="seo-empty" style={{ padding: 24 }}>All products have been generated. ✅</div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function SEODashboard() {
  const [tab, setTab]             = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadDashboard = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const url = force ? "/api/seo/dashboard?force_refresh=true" : "/api/seo/dashboard";
      const r   = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.success) setDashboard(d.data);
      else setError("SEO module returned an error");
    } catch (e) {
      setError("Cannot reach SEO module — is the backend running?");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await fetch("/api/seo/refresh", { method: "POST" });
      await loadDashboard(true);
    } finally {
      setRefreshing(false);
    }
  };

  const audit      = dashboard?.audit_summary      || {};
  const approval   = queueStats || dashboard?.approval_stats || {};
  const compliance = dashboard?.compliance_summary || {};
  const pendingCount = approval.pending || 0;

  return (
    <div className="seo-root">
      {/* Header */}
      <div className="seo-header">
        <div className="seo-header-left">
          <span className="seo-header-icon">🔍</span>
          <div>
            <div className="seo-header-title">SEO Intelligence</div>
            <div className="seo-header-sub">
              AI-powered · Enzo/Lizze priority · approval required before Shopify changes
              {dashboard?.from_cache && <span className="seo-cache-badge"> ⚡ cached</span>}
            </div>
          </div>
        </div>
        <button
          className="seo-btn seo-btn-ghost seo-btn-sm"
          onClick={refresh}
          disabled={refreshing}
        >
          {refreshing ? "⟳ Rebuilding…" : "↺ Refresh"}
        </button>
      </div>

      {/* Error / Loading */}
      {loading && !dashboard && (
        <div className="seo-loading-banner">
          <div className="seo-spinner" />
          Loading SEO data…
        </div>
      )}
      {error && !dashboard && (
        <div className="seo-error-banner">
          ⚠️ {error}
          <button className="seo-btn seo-btn-ghost seo-btn-sm" style={{ marginLeft: 16 }} onClick={() => loadDashboard()}>
            Retry
          </button>
        </div>
      )}

      {dashboard && (
        <>
          {/* KPI Strip */}
          <div className="seo-kpi-strip">
            <KpiCard
              label="Avg SEO Score"
              value={audit.average_score ?? "—"}
              sub={`${audit.total_products || 0} products · ${audit.priority_products || 0} priority`}
              color={(audit.average_score || 0) >= 80 ? "var(--seo-green)" : (audit.average_score || 0) >= 50 ? "#f5c518" : "#ff4d6d"}
            />
            <KpiCard
              label="Pending Approval"
              value={pendingCount}
              sub={approval.compliance_unsafe > 0 ? `${approval.compliance_unsafe} need review` : "all clear"}
              color={pendingCount > 0 ? "#f5c518" : "var(--seo-green)"}
            />
            <KpiCard
              label="Applied to Shopify"
              value={approval.applied || 0}
              sub={`${approval.total_changes || 0} total generated`}
              color="var(--seo-purple)"
            />
            <KpiCard
              label="Compliance Rate"
              value={`${compliance.overall_compliance_rate || 100}%`}
              sub={`${compliance.products_with_violations || 0} with issues`}
              color={(compliance.products_with_violations || 0) === 0 ? "var(--seo-green)" : "#ff4d6d"}
            />
            <KpiCard
              label="Priority Gaps"
              value={audit.priority_authority_gaps || 0}
              sub="Enzo/Lizze needing attention"
              color={(audit.priority_authority_gaps || 0) > 0 ? "#ff8c42" : "var(--seo-green)"}
            />
          </div>

          {/* Tabs */}
          <div className="seo-tabs">
            {TABS.map(t => (
              <button
                key={t.id}
                className={`seo-tab ${tab === t.id ? "active" : ""}`}
                onClick={() => setTab(t.id)}
              >
                {t.icon} {t.label}
                {t.id === "queue" && pendingCount > 0 && (
                  <span className="seo-tab-badge">{pendingCount}</span>
                )}
              </button>
            ))}
          </div>

          {/* Panels */}
          <div className="seo-panel">

            {/* OVERVIEW */}
            {tab === "overview" && (
              <div className="seo-overview-grid">
                <div className="seo-overview-score-card seo-card">
                  <div className="seo-card-header">Site SEO Health</div>
                  <div className="seo-overview-gauge-row">
                    <ScoreGauge score={audit.average_score || 0} size={140} label="Avg Score" />
                    <div className="seo-grade-breakdown">
                      <div className="seo-card-header" style={{ marginBottom: 8 }}>Grade Distribution</div>
                      <GradeBar distribution={audit.grade_distribution || {}} />
                      <div className="seo-grade-legend">
                        {Object.entries(audit.grade_distribution || {}).map(([g, n]) => (
                          <span key={g} className="seo-grade-legend-item">{g}: {n}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="seo-card">
                  <div className="seo-card-header">Approval Queue</div>
                  <div className="seo-approval-snapshot">
                    {[
                      { label: "Pending",  val: approval.pending || 0,  cls: "seo-text-warn" },
                      { label: "Approved", val: approval.approved || 0, cls: "seo-text-green" },
                      { label: "Applied",  val: approval.applied || 0,  cls: "seo-text-blue" },
                      { label: "Failed",   val: approval.failed || 0,   cls: "seo-text-red" },
                    ].map(({ label, val, cls }) => (
                      <div key={label} className="seo-approval-snap-item">
                        <span className={`seo-approval-snap-num ${cls}`}>{val}</span>
                        <span className="seo-approval-snap-lbl">{label}</span>
                      </div>
                    ))}
                  </div>
                  {pendingCount > 0 && (
                    <button
                      className="seo-btn seo-btn-primary"
                      style={{ marginTop: 12, width: "100%" }}
                      onClick={() => setTab("queue")}
                    >
                      Review {pendingCount} Pending Change{pendingCount !== 1 ? "s" : ""}
                    </button>
                  )}
                </div>

                <WorstProductsList
                  products={dashboard?.worst_products || []}
                  onGenerate={() => setTab("generate")}
                />

                <div className="seo-card">
                  <div className="seo-card-header">Compliance Status</div>
                  <div className="seo-approval-snapshot">
                    <div className="seo-approval-snap-item">
                      <span className="seo-approval-snap-num seo-text-green">
                        {compliance.compliant_products || audit.total_products || 0}
                      </span>
                      <span className="seo-approval-snap-lbl">Compliant</span>
                    </div>
                    <div className="seo-approval-snap-item">
                      <span className="seo-approval-snap-num seo-text-red">
                        {compliance.products_with_violations || 0}
                      </span>
                      <span className="seo-approval-snap-lbl">Issues</span>
                    </div>
                    <div className="seo-approval-snap-item">
                      <span className="seo-approval-snap-num seo-text-warn">
                        {compliance.total_violations || 0}
                      </span>
                      <span className="seo-approval-snap-lbl">Violations</span>
                    </div>
                  </div>
                  <button
                    className="seo-btn seo-btn-ghost"
                    style={{ marginTop: 12, width: "100%" }}
                    onClick={() => setTab("compliance")}
                  >
                    View Full Compliance Report
                  </button>
                </div>

                {/* Quick generate CTA */}
                <div className="seo-card" style={{ gridColumn: "1 / -1", textAlign: "center", padding: 24 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                    🏆 Ready to generate SEO for Enzo &amp; Lizze?
                  </div>
                  <div style={{ color: "var(--seo-muted)", marginBottom: 16 }}>
                    Priority products first — nothing touches Shopify until you approve each change.
                  </div>
                  <button
                    className="seo-btn seo-btn-primary"
                    style={{ minWidth: 220 }}
                    onClick={() => setTab("generate")}
                  >
                    ⚡ Go to Generate
                  </button>
                </div>
              </div>
            )}

            {tab === "generate" && (
              <GeneratePanel onJobComplete={() => { loadDashboard(); setTab("queue"); }} />
            )}

            {tab === "audit" && (
              <SEOAudit onChangeAdded={() => { loadDashboard(); setTab("queue"); }} />
            )}

            {tab === "queue" && (
              <ApprovalQueue onStatsChange={stats => setQueueStats(stats)} />
            )}

            {tab === "blog" && <BlogGenerator />}

            {tab === "compliance" && <ComplianceChecker />}
          </div>
        </>
      )}
    </div>
  );
}
